import type { IHttpClient } from '@wix/yoshi-flow-editor';
import { OrdersSettingsClient } from './ordersSettingsClient';
import type { FedopsLogger } from 'root/utils/monitoring/FedopsLogger';
import type { ReportError } from './utils/getMonitoredApiCall';
import { menusState } from 'root/states/MenusState';
import type { MenuAvailability } from '@wix/restaurants-olo-operations-client-commons';
import { MenuOrderingSettingsConverter } from '@wix/restaurants-olo-operations-client-commons';
import type { ErrorHandlerPublicAPI } from '@wix/error-handler';
import type { PanoramaErrorMonitor } from '@wix/panorama-client/dist/types/panorama-error-monitor';
import type { ErrorMonitorUserData } from '@wix/panorama-client/dist/types/types';
import type { IBIReporterService } from 'root/services/biReporterService';

export type GetMenuIdsByOperation = {
  menuIdsByOperation: string[] | undefined;
  menusAvailability: Record<string, MenuAvailability>;
};

export const PopulateMenuIdsByOperationClient = ({
  httpClient,
  errorHandler,
  reportError,
  errorMonitor,
  fedopsLogger,
  timezoneOffset = 0,
  biReporterService,
  useCalculateFirstAvailableAPI,
}: {
  httpClient: IHttpClient;
  errorHandler: ErrorHandlerPublicAPI;
  reportError?: ReportError;
  errorMonitor?: PanoramaErrorMonitor<ErrorMonitorUserData>;
  fedopsLogger?: FedopsLogger;
  timezoneOffset?: number;
  biReporterService?: IBIReporterService;
  useCalculateFirstAvailableAPI?: boolean;
}) => {
  const ordersSettingsClient = new OrdersSettingsClient(
    httpClient,
    errorHandler,
    errorMonitor,
    fedopsLogger,
    reportError,
    biReporterService,
    useCalculateFirstAvailableAPI
  );

  return {
    setTimezoneOffset: (newTimezoneOffset: number) => {
      timezoneOffset = newTimezoneOffset;
    },
    getAll: async (
      operationIdPromise?: Promise<string | undefined>
    ): Promise<GetMenuIdsByOperation | undefined> => {
      try {
        const operationId = await operationIdPromise;

        const fetchMenuIdsByOperation = async () => {
          const response = await ordersSettingsClient.getMenuOrderingSettingsByOperationId(
            operationId,
            () => {
              menusState.setHasError(true);
            }
          );

          return response.menuOrderingSettings || [];
        };

        const menuOrderingSettings = operationId ? await fetchMenuIdsByOperation() : undefined;
        const menusAvailability: Record<string, MenuAvailability> = {};

        menuOrderingSettings?.forEach((setting) => {
          const mos = MenuOrderingSettingsConverter.fromServerModel(setting, timezoneOffset, false);
          const menuId = mos.menuId;
          const availability = mos.availability;

          if (menuId && availability) {
            menusAvailability[menuId] = availability;
          }
        });

        return {
          menuIdsByOperation: Object.keys(menusAvailability),
          menusAvailability,
        };
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log('fetch menuOrdersSettings failed', e);
        menusState.setHasError(true);
      }
    },
  };
};
